import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaBed, FaWifi, FaMountain, FaSnowflake } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';

// Styled Components
const RoomsSection = styled.section`
  padding: 5rem 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #f59e0b;
  }
`;

const RoomsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const RoomCard = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;

const RoomImage = styled.div`
  height: 220px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const RoomPrice = styled.div`
  position: absolute;
  top: 20px;
  right: 0;
  background-color: #f59e0b;
  color: white;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 1.1rem;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const RoomContent = styled.div`
  padding: 1.5rem;
`;

const RoomTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: #1f2937;
`;

const RoomFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 1rem 0;
  gap: 1rem;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #4b5563;
  
  svg {
    margin-right: 0.5rem;
    color: #1e3a8a;
  }
`;

const BookButton = styled.button`
  display: inline-block;
  background-color: #1e3a8a;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  margin-top: 1rem;
  width: 100%;
  
  &:hover {
    background-color: #1e40af;
    transform: translateY(-2px);
  }
`;



const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const navigateToBooking = (roomType) => {
    navigate('/booking', { state: { selectedRoom: roomType } });
  };
  
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const roomsCollection = collection(db, 'rooms');
        const roomsSnapshot = await getDocs(roomsCollection);
        const roomsData = roomsSnapshot.docs.map(doc => {
          const data = doc.data();
          // Convert features from array of strings to array of objects with icons
          const featuresWithIcons = data.features ? data.features.map(feature => {
            let icon = <FaBed />;
            if (feature.toLowerCase().includes('wifi')) icon = <FaWifi />;
            else if (feature.toLowerCase().includes('view') || feature.toLowerCase().includes('garden')) icon = <FaMountain />;
            else if (feature.toLowerCase().includes('air')) icon = <FaSnowflake />;
            
            return { icon, text: feature };
          }) : [];
          
          return {
            id: doc.id,
            title: data.name || 'Room',
            price: data.price || '₹0',
            image: data.imageUrl || 'https://via.placeholder.com/300x200?text=Room+Image',
            features: featuresWithIcons
          };
        });
        setRooms(roomsData);
      } catch (error) {
        console.error('Error fetching rooms:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRooms();
  }, []);
  return (
    <RoomsSection id="rooms">
      <SectionTitle>Our Rooms</SectionTitle>
      <RoomsContainer>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', width: '100%' }}>
            <p>Loading rooms...</p>
          </div>
        ) : rooms.length > 0 ? (
          rooms.map((room) => (
            <RoomCard key={room.id}>
              <RoomImage image={room.image}>
                <RoomPrice>From {room.price}/night</RoomPrice>
              </RoomImage>
              <RoomContent>
                <RoomTitle>{room.title}</RoomTitle>
                <RoomFeatures>
                  {room.features && room.features.map((feature, index) => (
                    <Feature key={index}>
                      {feature.icon}
                      {feature.text}
                    </Feature>
                  ))}
                </RoomFeatures>
                <BookButton onClick={() => navigateToBooking(room.title)}>Book Now</BookButton>
              </RoomContent>
            </RoomCard>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', width: '100%' }}>
            <p>No rooms available. Add some in the admin panel.</p>
          </div>
        )}
      </RoomsContainer>
    </RoomsSection>
  );
};

export default Rooms;
